import { type RouteObject } from 'react-router-dom';
import { COVER_ROUTE, DATA_QUALITY_ROUTE, HIRE_WINDOW_ROUTE, HR_TIME_OFF_REQUESTS_ROUTE, LIST_PAGES, LOGIN_ROUTE, WHATS_NEW_ROUTE, isListDetailRecord, type PagedRecord } from '@bms2/shared';
import { AppShell } from './shell/AppShell';
import { NotFound } from './shell/Fallbacks';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { SHARE_LANDING_PATH, SharedFilePage } from './pages/SharedFilePage';
import { CERTIFICATE_UPLOAD_ROUTE, CertificateUploadPage } from './pages/CertificateUploadPage';
import { QUOTE_RESPONSE_ROUTE, QuoteResponsePage } from './pages/QuoteResponsePage';
import { DuplicatesPage, DUPLICATES_PAIR_ROUTE, DUPLICATES_ROUTE } from './pages/duplicates/DuplicatesPage';
import { DataReviewPage } from './pages/records/DataReviewPage';
import { AddressReviewPage, ADDRESS_REVIEW_ROUTE, CalendarRoute, CALENDAR_ROUTE, HygienePage, HYGIENE_ROUTE, ListPageRoute, RecordPageRoute } from './pages/records';
import { NdaAgreementPage, NdaReviewPage } from './pages/records/NdaReviewPage';
import { CoverRequiredPage } from './pages/records/CoverRequiredPage';
import { SAVED_VIEW_REDIRECTS, SavedViewRedirect } from './pages/records/SavedViewRedirect';
import { NDA_REVIEW_ROUTE } from './pages/records/NdaModal';
import { ListDetailRoute } from './pages/records/ListDetailPage';
import { QuoteDocumentRoute, WorkOrderDocumentRoute } from './pages/records/DocumentRoutes';
import { PurchaseOrderPage } from './pages/records/PurchaseOrderPage';
import { ServiceReportRoute, WorkOrderVisitRoute } from './pages/records/WorkOrderVisitPage';
import { JobChecklistRoute } from './pages/records/JobChecklistPage';
import { CHANGE_ORDER_ROUTE, ChangeOrderPageRoute } from './pages/records/ChangeOrderPage';
import { PACKAGE_ROUTE, PackagePageRoute } from './pages/records/PackagePage';
import { SalesOrderPage } from './pages/records/SalesOrderPage';
import { InvoicePage } from './pages/records/InvoicePage';
import { CreditNotePage } from './pages/records/CreditNotePage';
import { PaymentPage } from './pages/records/PaymentPage';
import { BillPage } from './pages/records/BillPage';
import { VendorCreditPage } from './pages/records/VendorCreditPage';
import { RfqComparisonPage } from './pages/records/RfqComparisonPage';
import { DealBomPage, DealBomRoute } from './pages/records/DealBomPage';
import { NEW_DEAL_ROUTE, NewDealPage } from './pages/records/NewDealPage';
import { StockCountPage, StockRevaluationPage, StockTransferPage } from './pages/records/StockDocumentPages';
import { TodosPage } from './pages/todos/TodosPage';
import { APPROVALS_ROUTE, PendingApprovalsPage } from './pages/approvals/PendingApprovalsPage';
import { TODOS_ROUTE } from './shell/Dashboard';
import { BLOCKED_POSTINGS_ROUTE, CHART_OF_ACCOUNTS_ROUTE, LEDGER_DRIFT_ROUTE, PERIODS_ROUTE, RECURRING_ROUTE, STATEMENTS_ROUTE, TAX_FORMS_ROUTE } from './pages/accounting/api';
import { ORG_CHART_ROUTE, ORG_EXCEPTIONS_ROUTE, OrgChartPage, OrgExceptionsPage } from './pages/org';
import { CallsPage, PHONE_CALLS_ROUTE } from './pages/phone/CallsPage';
import { PHONE_VOICEMAIL_ROUTE, VoicemailPage } from './pages/phone/VoicemailPage';
import { PHONE_TEXTS_ROUTE, TextsPage } from './pages/phone/TextsPage';
import { PHONE_THREAD_ROUTE, TextThreadPage } from './pages/phone/TextThreadPage';
import { CertificationsPage, ComplaintsPage, EmployeePage, EmployeesPage, HR_CERTIFICATIONS_ROUTE, HR_COMPLAINTS_ROUTE, HR_EMPLOYEE_ROUTE, HR_EMPLOYEES_ROUTE, MY_INFO_ROUTE, MyInfoPage, TIME_APPROVAL_ROUTE, TimeApprovalPage, TimeOffRequestsPage, UNEMPLOYMENT_CLAIMS_ROUTE, UnemploymentClaimsPage } from './pages/hr';
import {
  BANKING_ROUTE, BANK_ACCOUNT_ROUTE, CHECKS_ROUTE, DOCUMENTS_INBOX_ROUTE, EXPENSE_REPORTS_ROUTE, EXPENSES_ROUTE, OLD_EXPENSE_REPORTS_ROUTE, PAY_BILLS_ROUTE, RECONCILIATION_ROUTE,
} from './pages/banking/api';
import { PROJECT_PERFORMANCE_ROUTE, REPORTS_ROUTE, SALES_COMMISSION_ROUTE, SALES_DASHBOARD_ROUTE } from './pages/reports/api';
import { lazyGroup } from './shell/lazyGroup';

/**
 * Every screen sits inside the shell (pieces 12 and 13). The Dashboard is the
 * landing page at `/`; the four records carry the list and record routes of the
 * record-route table, which the seeded `NavItem` rows read from the same place;
 * a route resolving to no record and no nav item renders Not Found inside the
 * shell rather than as a blank page or a silent redirect.
 *
 * 🔨 `/locations/review` is declared before `/locations/:id`, because the review
 * queue's own path would otherwise be read as a building whose id is "review".
 */
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * 🔒 Nine route groups load on their first visit, one chunk each, behind the
 * shell outlet's one loading state (piece 12; decision 10580). The Login Screen,
 * the shell, the Dashboard and the record pages stay in the first download. Only
 * a group's route constants are read here, from its `api` module, so nothing of
 * its pages reaches the first chunk through this file.
 */
const settingsGroup = lazyGroup(() => import('./pages/settings/SettingsRoutes'));
const mailGroup = lazyGroup(() => import('./pages/mail'));
const knowledgeGroup = lazyGroup(() => import('./pages/knowledge'));
const bankingGroup = lazyGroup(() => import('./pages/banking'));
const accountingGroup = lazyGroup(() => import('./pages/accounting'));
const reportsGroup = lazyGroup(() => import('./pages/reports'));
const importGroup = lazyGroup(() => import('./pages/import/ImportPage'));
const assetsGroup = lazyGroup(() => import('./pages/assets'));
const workGroup = lazyGroup(() => import('./pages/work'));
// The change log's entries are 780 kB, so What's new loads with its page; the header's dot reads a build-time marker (T3600).
const whatsNewGroup = lazyGroup(() => import('./shell/WhatsNewPage'));

const WhatsNewPage = whatsNewGroup('WhatsNewPage');
const SettingsRoutes = settingsGroup('SettingsRoutes');
const MailPage = mailGroup('MailPage');
const QuarantinePage = mailGroup('QuarantinePage');
const MailSettingsWindow = mailGroup('MailSettingsWindow');
const MailboxRecordPage = mailGroup('MailboxRecordPage');
const ArticlesPage = knowledgeGroup('ArticlesPage');
const CategoriesPage = knowledgeGroup('CategoriesPage');
const ChecklistTemplatesPage = knowledgeGroup('ChecklistTemplatesPage');
const QuestionRedirectPage = knowledgeGroup('QuestionRedirectPage');
const PersonReportPage = knowledgeGroup('PersonReportPage');
const QuizPage = knowledgeGroup('QuizPage');
const QuizEditorPage = knowledgeGroup('QuizEditorPage');
const QuestionReportPage = knowledgeGroup('QuestionReportPage');
const ArticlePage = knowledgeGroup('ArticlePage');
const BankingPage = bankingGroup('BankingPage');
const BankAccountDetailPage = bankingGroup('BankAccountDetailPage');
const ReconciliationPage = bankingGroup('ReconciliationPage');
const ExpenseReportsPage = bankingGroup('ExpenseReportsPage');
const OldExpenseReportAddress = bankingGroup('OldExpenseReportAddress');
const ExpenseReportPage = bankingGroup('ExpenseReportPage');
const DocumentsInboxPage = bankingGroup('DocumentsInboxPage');
const PayBillsPage = bankingGroup('PayBillsPage');
const ChecksPage = bankingGroup('ChecksPage');
const ChartOfAccountsPage = accountingGroup('ChartOfAccountsPage');
const AccountPage = accountingGroup('AccountPage');
const JournalEntryRoute = accountingGroup('JournalEntryRoute');
const PeriodsPage = accountingGroup('PeriodsPage');
const RecurringPage = accountingGroup('RecurringPage');
const BlockedPostingsPage = accountingGroup('BlockedPostingsPage');
const DriftPage = accountingGroup('DriftPage');
const TaxFormsPage = accountingGroup('TaxFormsPage');
const StatementsRoute = accountingGroup('StatementsRoute');
const ProjectPerformancePage = reportsGroup('ProjectPerformancePage');
const SalesDashboardPage = reportsGroup('SalesDashboardPage');
const SalesCommissionPage = reportsGroup('SalesCommissionPage');
const ReportsIndexPage = reportsGroup('ReportsIndexPage');
const ReportPage = reportsGroup('ReportPage');
const ImportPage = importGroup('ImportPage');
const AssetsPage = assetsGroup('AssetsPage');
const AssetTemplatesPage = assetsGroup('AssetTemplatesPage');
const BomConversionPage = assetsGroup('BomConversionPage');
const DispatchBoardPage = workGroup('DispatchBoardPage');
const SchedulePage = workGroup('SchedulePage');
const OnCallPage = workGroup('OnCallPage');
const MyJobsPage = workGroup('MyJobsPage');
const AgreementsPage = workGroup('AgreementsPage');
const CoveragePage = workGroup('CoveragePage');
const EmergencyIntakePage = workGroup('EmergencyIntakePage');
const FieldReportsPage = workGroup('FieldReportsPage');

/**
 * 🔨 The Settings group mounts at these three bases: its own `/settings` and the
 * two addresses `settingsRoutes` serves outside it, the Trash and the tax
 * register's old address. A Settings route under none of them would never draw,
 * and the route table's test fails on one.
 */
export const SETTINGS_GROUP_BASES = ['settings', 'admin/trash', 'accounting/tax'] as const;

/**
 * 🔒 A list-backed record renders as one list-and-detail page at both of its
 * routes, so the list stays mounted as the pick changes (decision 0408,
 * generalising decision 0318 off the catalog). The Deals list and the Lead
 * queue keep the two-page pattern, because each carries a second whole-width
 * rendering a third of the width does not hold (piece 13, Open issues).
 *
 * 🔨 The Purchase Order's detail is its own document page, not a tile canvas
 * (piece 22); every other record's is the Record Page its piece built.
 */
function RecordDetail({ recordType, recordId }: { recordType: PagedRecord; recordId: string }) {
  // The three stock documents draw their own pages (piece 22; decision 0441): a transfer and a count carry a table, and a revaluation is read back as posted.
  if (recordType === 'StockTransfer') return <StockTransferPage transferId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'StockCount') return <StockCountPage countId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'StockRevaluation') return <StockRevaluationPage revaluationId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'PurchaseOrder') return <PurchaseOrderPage orderId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'Bill') return <BillPage billId={recordId} apiBaseUrl={apiBaseUrl} />;
  // Decision 7580: the Vendor Credit's own document page, which its list row and the mail reading block's link open.
  if (recordType === 'VendorCredit') return <VendorCreditPage creditId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'Quote') return <QuoteDocumentRoute recordId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'WorkOrder') return <WorkOrderDocumentRoute recordId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'SalesOrder') return <SalesOrderPage orderId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'Invoice') return <InvoicePage invoiceId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'CreditNote') return <CreditNotePage noteId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'Payment') return <PaymentPage paymentId={recordId} apiBaseUrl={apiBaseUrl} />;
  // Decision 0761: the Manual Journal's own document page, or its form where + New opened it.
  if (recordType === 'JournalEntry') return <JournalEntryRoute entryId={recordId} apiBaseUrl={apiBaseUrl} />;
  // Decision 1052: the RFQ's comparison and the bill of materials' page, each drawn beside its list.
  if (recordType === 'RFQ') return <RfqComparisonPage rfqId={recordId} apiBaseUrl={apiBaseUrl} />;
  if (recordType === 'BOM') return <DealBomPage bomId={recordId} apiBaseUrl={apiBaseUrl} />;
  return <RecordPageRoute recordType={recordType} apiBaseUrl={apiBaseUrl} />;
}

function detailFor(recordType: PagedRecord) {
  // 🔨 Keyed on the record, so opening a second one from the list mounts a fresh page rather than reusing the first record's drafts.
  return function detail(recordId: string) {
    return <RecordDetail key={recordId} recordType={recordType} recordId={recordId} />;
  };
}

/**
 * Piece 22's sales side and its purchase and stock side each carry their list
 * here and their record page with the document-page stream. The generic record
 * route would draw a tile canvas over a record with no tiles, and a record route
 * with no page behind it reads as a broken record rather than as Not Found, so
 * the rest carry their list route alone until that stream reaches them. The
 * four sales-side documents and the Bill left this list on 2026-09-15, when
 * their own pages landed, and the RFQ and the bill of materials left it on
 * 2026-09-27 (decision 1052). The Vendor Credit left it on 2026-10-25, when its
 * own page landed (decision 7580, reversing 1158).
 */
const LIST_ONLY_RECORDS: readonly PagedRecord[] = [
  // 🔒 Decision 2910: a Service Report's page is its visit's under the job; its record route below finds the job and goes there.
  'ServiceReport',
];

function isListOnlyRecord(recordType: PagedRecord): boolean {
  return LIST_ONLY_RECORDS.includes(recordType);
}

const recordRoutes: RouteObject[] = Object.values(LIST_PAGES).flatMap((spec) =>
  isListDetailRecord(spec.recordType)
    ? [{ path: `${spec.route.slice(1)}/:id?`, element: <ListDetailRoute recordType={spec.recordType} detail={detailFor(spec.recordType)} apiBaseUrl={apiBaseUrl} /> }]
    : isListOnlyRecord(spec.recordType)
    ? [{ path: spec.route.slice(1), element: <ListPageRoute recordType={spec.recordType} apiBaseUrl={apiBaseUrl} /> }]
    : [
        { path: spec.route.slice(1), element: <ListPageRoute recordType={spec.recordType} apiBaseUrl={apiBaseUrl} /> },
        { path: `${spec.route.slice(1)}/:id`, element: <RecordPageRoute recordType={spec.recordType} apiBaseUrl={apiBaseUrl} /> },
      ],
);

export const baseRoutes: RouteObject[] = [
  // 🔨 The Login Screen is a page outside the shell, never a modal (piece 14).
  { path: LOGIN_ROUTE, element: <LoginPage apiBaseUrl={apiBaseUrl} /> },
  // 🔨 The share link's landing page is outside the shell too (piece 9; decision 0361): its
  // reader may hold no session yet, and the address the mail carries had no page at all.
  { path: `${SHARE_LANDING_PATH}/:linkId`, element: <SharedFilePage apiBaseUrl={apiBaseUrl} /> },
  // 🔨 Decision 0679: the certificate request link's page is outside the shell too; the contact who opens it has no login.
  { path: `${CERTIFICATE_UPLOAD_ROUTE}/:token`, element: <CertificateUploadPage apiBaseUrl={apiBaseUrl} /> },
  // 🔨 Decision 2650: the page the Quote link opens is outside the shell too; the customer who answers it has no login.
  { path: `${QUOTE_RESPONSE_ROUTE}/:token`, element: <QuoteResponsePage apiBaseUrl={apiBaseUrl} /> },
  {
    path: '/',
    element: <AppShell apiBaseUrl={apiBaseUrl} />,
    children: [
      { index: true, element: <DashboardPage apiBaseUrl={apiBaseUrl} /> },
      { path: ADDRESS_REVIEW_ROUTE.slice(1), element: <AddressReviewPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Piece 10, decision 1484: the Duplicates report, the page `queue:duplicate-review` names; static, so /companies/:id never swallows it.
      { path: DUPLICATES_ROUTE.slice(1), element: <DuplicatesPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 T3422: the same report opened on one pair, as the import's cleanup list links a set-aside Weak pair.
      { path: `${DUPLICATES_PAIR_ROUTE.slice(1)}/:firstId/:secondId`, element: <DuplicatesPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 5382: Records Needing Data Review, the page `queue:data-quality` names.
      { path: DATA_QUALITY_ROUTE.slice(1), element: <DataReviewPage apiBaseUrl={apiBaseUrl} /> },
      ...recordRoutes,
      // 🔒 The Lead queue's two filters and the Companies list's three kinds are built-in views now (decision 2880): each old address opens its list on that view, so a kept link still lands.
      ...SAVED_VIEW_REDIRECTS.map((redirect) => ({ path: redirect.from, element: <SavedViewRedirect list={redirect.list} view={redirect.view} /> })),
      { path: HYGIENE_ROUTE.slice(1), element: <HygienePage apiBaseUrl={apiBaseUrl} /> },
      // Piece 18, decision 1092: the Deal's old address for a list redirects to the list's one home,
      // `/boms/:id`, naming the Deal in the query. `/rfqs/:id` and `/boms/:id` are raised from the
      // record-route table above.
      { path: 'deals/:dealId/boms/:bomId', element: <DealBomRoute /> },
      // 🔒 Piece 18, decision 2510: New Deal as a page at its own address; a static segment, so /deals/:id never swallows it.
      { path: NEW_DEAL_ROUTE.slice(1), element: <NewDealPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 22, decision 0475: the Change Order's own document page, reached from its parent
      // Sales Order's list and from Raise a change order there. It has no list of its own.
      { path: `${CHANGE_ORDER_ROUTE.slice(1)}/:id`, element: <ChangeOrderPageRoute apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 1730: a package's page, reached from its order's Packages block; it has no list of its own.
      { path: `${PACKAGE_ROUTE.slice(1)}/:id`, element: <PackagePageRoute apiBaseUrl={apiBaseUrl} /> },
      // Piece 9's NDA review queue, the dashboard's "NDAs in review", and one agreement's questionnaire (built 2026-09-12).
      { path: NDA_REVIEW_ROUTE.slice(1), element: <NdaReviewPage apiBaseUrl={apiBaseUrl} /> },
      { path: `${NDA_REVIEW_ROUTE.slice(1)}/:id`, element: <NdaAgreementPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 9680: the page a file the cover refuses opens on in a browser, naming the account and the person, with Request Cover.
      { path: COVER_ROUTE.slice(1), element: <CoverRequiredPage apiBaseUrl={apiBaseUrl} /> },
      { path: CALENDAR_ROUTE.slice(1), element: <CalendarRoute apiBaseUrl={apiBaseUrl} /> },
      // Piece 28: the asset register, and `/assets/:id` as each node's permanent address.
      { path: 'assets', element: <AssetsPage apiBaseUrl={apiBaseUrl} /> },
      // Decision 1293: the asset template library, a literal segment beside the node's permanent address.
      { path: 'assets/templates', element: <AssetTemplatesPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'assets/:id', element: <AssetsPage apiBaseUrl={apiBaseUrl} /> },
      // Decision 0721: the won Deal's parts list converted to assets, the page the Won to-do opens.
      { path: 'assets/convert/:dealId', element: <BomConversionPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 29: the work-order side's own pages.
      // 🔒 T1263: a visit's own page, under its job; the Visits tile's rows open it.
      { path: 'work-orders/:id/visits/:visitId', element: <WorkOrderVisitRoute apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 2910: a Service Reports row opens here, and the route goes on to the visit's own page under its job.
      { path: 'service-reports/:id', element: <ServiceReportRoute apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 2111 (T889): the job's own checklist, answered and signed off.
      { path: 'work-orders/:id/checklist', element: <JobChecklistRoute apiBaseUrl={apiBaseUrl} /> },
      { path: 'work/dispatch', element: <DispatchBoardPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'work/schedule', element: <SchedulePage apiBaseUrl={apiBaseUrl} /> },
      // Decision 4000 (T390): the on-call roster, under Work beside the board and the Schedule.
      { path: 'work/on-call', element: <OnCallPage apiBaseUrl={apiBaseUrl} /> },
      // Decision 1909: My Jobs, the technician's own board, beside the Dispatch Board.
      { path: 'work/my-jobs', element: <MyJobsPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'work/agreements', element: <AgreementsPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'work/agreements/:id', element: <AgreementsPage apiBaseUrl={apiBaseUrl} /> },
      // Decision 0735: the page the won Deal's coverage question opens.
      { path: 'work/coverage/:dealId', element: <CoveragePage apiBaseUrl={apiBaseUrl} /> },
      { path: 'work/emergency', element: <EmergencyIntakePage apiBaseUrl={apiBaseUrl} /> },
      { path: 'work/reports', element: <FieldReportsPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 32: the mail client and its quarantine review; the literal segment stands beside the client's own address.
      { path: 'mail', element: <MailPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'mail/quarantine', element: <QuarantinePage apiBaseUrl={apiBaseUrl} /> },
      // T419, decision 1235: the Mail Settings window, on its first section or the one the address names.
      { path: 'mail/settings', element: <MailSettingsWindow apiBaseUrl={apiBaseUrl} /> },
      { path: 'mail/settings/:section', element: <MailSettingsWindow apiBaseUrl={apiBaseUrl} /> },
      // Piece 32 part c, decision 0840: a mailbox's record page and its Grants panel.
      { path: 'mail/mailboxes/:mailboxId', element: <MailboxRecordPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 30: the knowledge base; the literal manage and checklists segments stand before the article's own `/kb/:id`.
      { path: 'kb', element: <ArticlesPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'kb/manage', element: <CategoriesPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 2281: the Checklists tab, where the checklist templates are edited since they left Settings › Service.
      { path: 'kb/checklists', element: <ChecklistTemplatesPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'kb/questions/:id', element: <QuestionRedirectPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'kb/people/:personId', element: <PersonReportPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'kb/:id/quiz', element: <QuizPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'kb/:id/quiz/edit', element: <QuizEditorPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'kb/:id/quiz/report', element: <QuestionReportPage apiBaseUrl={apiBaseUrl} /> },
      { path: 'kb/:id', element: <ArticlePage apiBaseUrl={apiBaseUrl} /> },
      // Piece 9's To-Do page, the role queue's own route and the My to-dos tile's link (decision 0344).
      { path: TODOS_ROUTE.slice(1), element: <TodosPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 22's Pending Approvals queue, the route the `queue:pending-approvals` row names (piece 8; piece 12).
      { path: APPROVALS_ROUTE.slice(1), element: <PendingApprovalsPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 23: the ledger's screens under ACCOUNTING and the blocked-postings queue's page.
      { path: CHART_OF_ACCOUNTS_ROUTE.slice(1), element: <ChartOfAccountsPage apiBaseUrl={apiBaseUrl} /> },
      { path: `${CHART_OF_ACCOUNTS_ROUTE.slice(1)}/:id`, element: <AccountPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 0761: the Manual Journal is a list-and-detail page raised from the record-route table above, at its old address.
      { path: PERIODS_ROUTE.slice(1), element: <PeriodsPage apiBaseUrl={apiBaseUrl} /> },
      { path: RECURRING_ROUTE.slice(1), element: <RecurringPage apiBaseUrl={apiBaseUrl} /> },
      { path: BLOCKED_POSTINGS_ROUTE.slice(1), element: <BlockedPostingsPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 24: the banking side's three screens, the routes the seeded NavItem rows name.
      { path: BANKING_ROUTE.slice(1), element: <BankingPage apiBaseUrl={apiBaseUrl} /> },
      { path: `${BANK_ACCOUNT_ROUTE.slice(1)}/:id`, element: <BankAccountDetailPage apiBaseUrl={apiBaseUrl} /> },
      { path: `${RECONCILIATION_ROUTE.slice(1)}/:id`, element: <ReconciliationPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 1310: the section is Expenses, and a report lives inside it; the old addresses and the bare reports path redirect.
      { path: EXPENSES_ROUTE.slice(1), element: <ExpenseReportsPage apiBaseUrl={apiBaseUrl} /> },
      { path: EXPENSE_REPORTS_ROUTE.slice(1), element: <OldExpenseReportAddress /> },
      { path: `${EXPENSE_REPORTS_ROUTE.slice(1)}/:id`, element: <ExpenseReportPage apiBaseUrl={apiBaseUrl} /> },
      { path: OLD_EXPENSE_REPORTS_ROUTE.slice(1), element: <OldExpenseReportAddress /> },
      { path: `${OLD_EXPENSE_REPORTS_ROUTE.slice(1)}/:id`, element: <OldExpenseReportAddress /> },
      // 🔒 The route the `queue:documents-inbox` NavItem names; its receipts half is built, walked by the Edit Expense window (decision 1187).
      { path: DOCUMENTS_INBOX_ROUTE.slice(1), element: <DocumentsInboxPage apiBaseUrl={apiBaseUrl} /> },
      { path: PAY_BILLS_ROUTE.slice(1), element: <PayBillsPage apiBaseUrl={apiBaseUrl} /> },
      { path: CHECKS_ROUTE.slice(1), element: <ChecksPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 10's org chart and its exceptions queue (decision 0079). 🔨 The queue's
      // static path is declared before the chart, so neither route swallows the other.
      { path: ORG_EXCEPTIONS_ROUTE.slice(1), element: <OrgExceptionsPage apiBaseUrl={apiBaseUrl} /> },
      { path: ORG_CHART_ROUTE.slice(1), element: <OrgChartPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 34, decision 1467: the HR group's pages, each refused by its own route without `hr.employees.view`.
      { path: HR_EMPLOYEES_ROUTE.slice(1), element: <EmployeesPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 3181: the Hire window's own address, HR › Employees with the window open; keyed so it opens afresh.
      { path: HIRE_WINDOW_ROUTE.slice(1), element: <EmployeesPage key="hire" apiBaseUrl={apiBaseUrl} openHire /> },
      // 🔒 Decision 3180: one job's employee file, and the signed-in person's own from the user menu.
      { path: HR_EMPLOYEE_ROUTE.slice(1), element: <EmployeePage apiBaseUrl={apiBaseUrl} /> },
      // Decision 10651: one job's unemployment claims, under its Employee page.
      { path: UNEMPLOYMENT_CLAIMS_ROUTE.slice(1), element: <UnemploymentClaimsPage apiBaseUrl={apiBaseUrl} /> },
      { path: MY_INFO_ROUTE.slice(1), element: <MyInfoPage apiBaseUrl={apiBaseUrl} /> },
      { path: HR_CERTIFICATIONS_ROUTE.slice(1), element: <CertificationsPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 1468: a complainant reaches their own complaints here too; the route answers what canViewHrRecord reaches.
      { path: HR_COMPLAINTS_ROUTE.slice(1), element: <ComplaintsPage apiBaseUrl={apiBaseUrl} /> },
      { path: TIME_APPROVAL_ROUTE.slice(1), element: <TimeApprovalPage apiBaseUrl={apiBaseUrl} /> },
      // 🔒 Decision 13250: the approver's time-off requests alone, for a manager who holds no time.approve; the route answers what the chain decides.
      { path: HR_TIME_OFF_REQUESTS_ROUTE.slice(1), element: <TimeOffRequestsPage apiBaseUrl={apiBaseUrl} /> },
      // Decision 11860: the phone system's pages, Calls, Voicemail and Texts with one conversation's thread.
      { path: PHONE_CALLS_ROUTE.slice(1), element: <CallsPage apiBaseUrl={apiBaseUrl} /> },
      { path: PHONE_VOICEMAIL_ROUTE.slice(1), element: <VoicemailPage apiBaseUrl={apiBaseUrl} /> },
      { path: PHONE_TEXTS_ROUTE.slice(1), element: <TextsPage apiBaseUrl={apiBaseUrl} /> },
      { path: PHONE_THREAD_ROUTE.slice(1), element: <TextThreadPage apiBaseUrl={apiBaseUrl} /> },
      { path: LEDGER_DRIFT_ROUTE.slice(1), element: <DriftPage apiBaseUrl={apiBaseUrl} /> },
      // 🔨 Piece 25, decision 0707: the tax register is the first section of Settings › Tax › Sales Tax, and its
      // old address here redirects there through the Settings routes' retired map.
      // Decision 0677: the forms library, reached from the certificate window and from Settings › Tax.
      { path: TAX_FORMS_ROUTE.slice(1), element: <TaxFormsPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 27, decision 0223: the customer statement, reached from a customer's own page with the customer in the query.
      { path: STATEMENTS_ROUTE.slice(1), element: <StatementsRoute apiBaseUrl={apiBaseUrl} /> },
      // Piece 26, decision 0509: the Reports list and one report's page, with the two dashboards and the
      // Sales Commission Report beside them.
      // 🔨 Every named screen is declared BEFORE `/reports/:key`, or each would be read as a report whose key is its own slug.
      { path: PROJECT_PERFORMANCE_ROUTE.slice(1), element: <ProjectPerformancePage apiBaseUrl={apiBaseUrl} /> },
      { path: SALES_DASHBOARD_ROUTE.slice(1), element: <SalesDashboardPage apiBaseUrl={apiBaseUrl} /> },
      // The route a rep's bar on the sales dashboard links to with their employment.
      { path: SALES_COMMISSION_ROUTE.slice(1), element: <SalesCommissionPage apiBaseUrl={apiBaseUrl} /> },
      { path: REPORTS_ROUTE.slice(1), element: <ReportsIndexPage apiBaseUrl={apiBaseUrl} /> },
      { path: `${REPORTS_ROUTE.slice(1)}/:key`, element: <ReportPage apiBaseUrl={apiBaseUrl} /> },
      // Piece 12, decision 0636: the change log, reached from the avatar menu. It reads no API, and an
      // outside contact's session reaches it too: nothing in it names a customer or a record.
      { path: WHATS_NEW_ROUTE.slice(1), element: <WhatsNewPage /> },
      // Piece 31, decision 0922: the importer's one screen, SYSTEM's Import row.
      { path: 'admin/import', element: <ImportPage apiBaseUrl={apiBaseUrl} /> },
      // Settings (piece 15): the Index and every entry's page, inside the shell, loaded on the group's first visit.
      ...SETTINGS_GROUP_BASES.map((base) => ({ path: `${base}/*`, element: <SettingsRoutes base={base} apiBaseUrl={apiBaseUrl} /> })),
      { path: '*', element: <NotFound /> },
    ],
  },
];
